import { useCallback, useEffect, useState } from "react";
import useSWR from "swr";
import { Link, useParams } from "react-router-dom";
import { ChevronLeft, ExternalLink, Video } from "lucide-react";
import { useAdminHeader } from "../AdminShell";
import { apiGet, apiGetEnvelope } from "../api";
import { formatDate, PageError, stageLabel } from "../ui-helpers";
import { PlaceEditorSheet } from "./PlaceEditorPage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ---------- per-place result shapes (mirror worker getRunPlaces) ----------
type Clip = { url: string | null; creator: string | null; gifUrl: string | null; poster: string | null };
type PlaceResult =
  | { kind: "media"; status: string; clips: Clip[]; cover: string | null; auditSummary: string | null }
  | { kind: "classify"; status: string; classification: Record<string, unknown> }
  | { kind: "hygiene"; status: string; verdict: string; reason: string | null; qualityFlags: string[]; reviewedBy: string | null; signals: Array<Record<string, unknown>> }
  | { kind: "published"; status: string; published: boolean; composite: number | null; cover: string | null; scores: Record<string, number | null> | null }
  | { kind: "reviews"; status: string; reviewCount: number }
  | { kind: "other"; status: string };
type RunPlace = { id: string; name: string; neighborhood: string | null; result: PlaceResult; logs: Array<Record<string, unknown>> };
type RunData = { run: Record<string, unknown>; logs: Array<Record<string, unknown>>; places?: Array<{ id: string; name: string }>; stages?: Array<Record<string, unknown>> };

const PAGE = 60;

export function RunDetailPage() {
  const { id } = useParams();
  const { data, error } = useSWR<RunData>(id ? `/admin/api/runs/${id}` : null, apiGet, { refreshInterval: 5000 });
  useAdminHeader({
    title: data ? `Run ${String(data.run.id)}` : `Run ${id ?? ""}`,
    breadcrumbs: [{ label: "Runs", to: "/runs" }, { label: id ?? "Run" }],
    actions: (
      <div className="flex items-center gap-2">
        {data && <StateBadge state={String(data.run.state)} />}
        <Link to="/runs" className="inline-flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-sm font-medium hover:bg-muted"><ChevronLeft className="size-4" /> Runs</Link>
      </div>
    ),
  }, [data?.run.id, data?.run.state, id]);
  if (error) return <PageError error={error} />;
  if (!data) return <div className="mx-auto w-full max-w-[1500px] p-6"><Skeleton className="h-96" /></div>;
  return <RunDetail id={id!} data={data} />;
}

function RunDetail({ id, data }: { id: string; data: RunData }) {
  const run = data.run;
  const stages = data.stages ?? [];
  const multiStage = stages.length > 1;
  const [tab, setTab] = useState("places");
  const [openPlaceId, setOpenPlaceId] = useState<string | null>(null);
  return (
    <div className="mx-auto grid w-full max-w-[1500px] gap-4 p-6">
      {/* Run status — compact, one line (the overall status lives up here, not in a big card). */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-semibold">{stageLabel(run.stage)}</span>
        <Dot /><span className="text-muted-foreground">{String(run.citySlug)}</span>
        <Dot /><span className="tabular-nums">{String(run.done ?? 0)}/{String(run.total ?? 0)} done{Number(run.cut) ? `, ${String(run.cut)} cut` : ""}</span>
        <Dot /><span className="text-muted-foreground">{formatDate(run.startedAt)}</span>
        <Dot /><span className="text-muted-foreground tabular-nums">{duration(run.startedAt, run.finishedAt)}</span>
        {typeof run.error === "string" && run.error && <><Dot /><span className="text-destructive">{run.error}</span></>}
      </div>
      <ScopeLine places={data.places} />

      <Tabs value={tab} onValueChange={setTab} className="grid gap-3">
        <TabsList className="h-9 w-fit">
          <TabsTrigger value="places">Places</TabsTrigger>
          {multiStage && <TabsTrigger value="stages">Stages ({stages.length})</TabsTrigger>}
          <TabsTrigger value="logs">Logs ({data.logs.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="places" className="m-0">
          <RunPlaces runId={id} onOpenPlace={setOpenPlaceId} />
        </TabsContent>

        {multiStage && (
          <TabsContent value="stages" className="m-0">
            <div className="flex flex-wrap gap-2">
              {stages.map((stage) => (
                <div key={String(stage.id)} className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm ${stage.id === run.id ? "border-primary bg-primary/5" : ""}`}>
                  <span className={`size-2 rounded-full ${stateDot(String(stage.state))}`} />
                  <span className="font-medium">{stageLabel(stage.stage)}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{String(stage.done ?? 0)}/{String(stage.total ?? 0)}</span>
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        <TabsContent value="logs" className="m-0">
          <div className="overflow-auto rounded-lg border">
            <Table>
              <TableHeader><TableRow><TableHead className="w-40">Time</TableHead><TableHead className="w-20">Level</TableHead><TableHead className="w-40">Step</TableHead><TableHead>Message</TableHead></TableRow></TableHeader>
              <TableBody>{data.logs.map((log) => <TableRow key={String(log.id)}><TableCell className="whitespace-nowrap text-xs text-muted-foreground">{formatDate(log.createdAt)}</TableCell><TableCell><Badge variant="outline">{String(log.level ?? "")}</Badge></TableCell><TableCell className="text-xs">{String(log.step ?? "")}</TableCell><TableCell>{String(log.message ?? "")}</TableCell></TableRow>)}</TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <PlaceEditorSheet slug={String(run.citySlug)} placeId={openPlaceId} onOpenChange={(open) => { if (!open) setOpenPlaceId(null); }} />
    </div>
  );
}

function ScopeLine({ places }: { places?: Array<{ id: string; name: string }> }) {
  const list = places ?? [];
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-muted-foreground">Scope:</span>
      <span className="font-medium">{list.length === 0 ? "Whole city" : list.length === 1 ? "1 place" : `${list.length} places`}</span>
      {list.length > 0 && list.length <= 6 && list.map((p) => <Badge key={p.id} variant="outline" className="font-normal" title={p.id}>{p.name}</Badge>)}
    </div>
  );
}

// The core of the run: every place it touched, dense, with the actual result.
function RunPlaces({ runId, onOpenPlace }: { runId: string; onOpenPlace: (id: string) => void }) {
  const [places, setPlaces] = useState<RunPlace[]>([]);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [family, setFamily] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async (reset: boolean) => {
    setLoading(true);
    setLoadError(null);
    try {
      const off = reset ? 0 : offset;
      const env = await apiGetEnvelope<{ stage: string; family: string; places: RunPlace[] }>(`/admin/api/runs/${runId}/places?limit=${PAGE}&offset=${off}`);
      const batch = env.data.places;
      setPlaces((prev) => (reset ? batch : [...prev, ...batch]));
      setFamily(env.data.family);
      setTotal(Number(env.meta?.total ?? batch.length));
      setOffset(off + batch.length);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [runId, offset]);

  useEffect(() => { setPlaces([]); setOffset(0); setTotal(0); load(true); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [runId]);

  return (
    <div className="grid gap-2">
      <p className="text-xs text-muted-foreground">{total ? `${places.length} of ${total} places` : loading ? "Loading…" : "No places"} — the actual {family || "stage"} result for each.</p>
      {loadError && <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">{loadError}</div>}
      {loading && places.length === 0 ? (
        <div className="grid gap-2">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-14" />)}</div>
      ) : (
        <div className="divide-y rounded-lg border">
          {places.map((place) => <PlaceRow key={place.id} place={place} onOpen={onOpenPlace} />)}
        </div>
      )}
      {places.length < total && (
        <div><Button variant="outline" size="sm" disabled={loading} onClick={() => load(false)}>{loading ? "Loading…" : `Load more (${total - places.length} left)`}</Button></div>
      )}
    </div>
  );
}

function PlaceRow({ place, onOpen }: { place: RunPlace; onOpen: (id: string) => void }) {
  return (
    <div className="flex flex-wrap items-start gap-x-4 gap-y-2 px-3 py-2.5">
      <button type="button" onClick={() => onOpen(place.id)} className="flex w-56 shrink-0 items-start gap-2 rounded-md text-left transition-colors hover:bg-muted/60" title="Open place">
        <ResultStatus result={place.result} />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium underline-offset-2 hover:underline">{place.name}</div>
          <div className="truncate text-xs text-muted-foreground">{place.neighborhood || place.id}</div>
        </div>
      </button>
      <div className="min-w-0 flex-1">
        <ResultInline result={place.result} />
        {place.logs.length > 0 && (
          <details className="mt-1.5 text-xs">
            <summary className="cursor-pointer text-muted-foreground">{place.logs.length} log line{place.logs.length === 1 ? "" : "s"}</summary>
            <div className="mt-1 grid gap-0.5">
              {place.logs.map((log, i) => <div key={i} className="flex gap-2"><span className="shrink-0 text-muted-foreground">{String(log.step ?? log.level ?? "")}</span><span className="min-w-0">{String(log.message ?? "")}</span></div>)}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function ResultInline({ result }: { result: PlaceResult }) {
  if (result.kind === "media") {
    if (result.clips.length) {
      return (
        <div className="flex flex-wrap gap-1.5">
          {result.clips.map((clip, i) => (
            <a key={i} href={clip.url || clip.gifUrl || "#"} target="_blank" rel="noopener noreferrer" className="group relative h-20 w-[45px] overflow-hidden rounded bg-black ring-1 ring-border" title={clip.creator || "Open clip"}>
              {clip.poster && <img src={clip.poster} alt="" className="absolute inset-0 h-full w-full object-cover opacity-80" />}
              {clip.gifUrl ? <img src={clip.gifUrl} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="grid h-full place-items-center text-white/50"><Video className="size-3" /></div>}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-0.5 bg-gradient-to-t from-black/80 to-transparent px-0.5 pb-0.5 text-[9px] font-medium text-white"><ExternalLink className="size-2 shrink-0" /></span>
            </a>
          ))}
        </div>
      );
    }
    return <p className="text-sm text-muted-foreground">{result.status === "not-run" ? "Discovery hasn't run." : "No creator clips found."}{result.auditSummary ? ` ${result.auditSummary}` : ""}</p>;
  }
  if (result.kind === "classify") {
    const c = result.classification;
    const chips = [...arr(c.cuisine), ...arr(c.vibeTags), ...arr(c.mood)].slice(0, 10);
    const kvAll: Array<[string, unknown]> = [["Format", c.format], ["Price", c.priceTier], ["Meal", c.meal], ["Group", c.groupType], ["Noise", c.noiseLevel]];
    const kv = kvAll.filter(([, v]) => Boolean(v));
    if (result.status === "missing") return <p className="text-sm text-muted-foreground">Not classified.</p>;
    return (
      <div className="grid gap-1.5">
        {kv.length > 0 && <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs">{kv.map(([k, v]) => <span key={k}><span className="text-muted-foreground">{k} </span><span className="font-medium">{String(v)}</span></span>)}</div>}
        {chips.length > 0 && <Chips values={chips} />}
      </div>
    );
  }
  if (result.kind === "hygiene") {
    if (result.status === "missing") return <p className="text-sm text-muted-foreground">No hygiene verdict.</p>;
    return (
      <div className="grid gap-1 text-sm">
        {result.reason && <p className="text-muted-foreground">{result.reason}</p>}
        {result.qualityFlags?.length > 0 && <Chips values={result.qualityFlags} />}
        {result.signals?.length > 0 && <div className="text-xs text-muted-foreground">{result.signals.map((s, i) => <span key={i} className="mr-3">{String(s.signalType)} · {String(s.lens)} ×{String(s.mentionCount)}{s.corroborated ? " ✓" : ""}</span>)}</div>}
      </div>
    );
  }
  if (result.kind === "published") {
    const s = result.scores;
    if (result.status === "missing") return <p className="text-sm text-muted-foreground">Not published.</p>;
    return (
      <div className="flex flex-wrap items-center gap-3">
        {result.cover && <img src={result.cover} alt="" className="size-10 rounded object-cover ring-1 ring-border" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
        {result.composite != null && <span className="text-sm"><span className="font-semibold tabular-nums">{result.composite.toFixed(1)}</span> <span className="text-muted-foreground">composite</span></span>}
        {s && <span className="flex flex-wrap gap-x-3 text-xs">{([["Food", s.food], ["Vibe", s.vibe], ["Value", s.value], ["Amenities", s.amenities], ["Access", s.access]] as Array<[string, number | null]>).filter(([, v]) => v != null).map(([k, v]) => <span key={k}><span className="text-muted-foreground">{k} </span><span className="font-medium tabular-nums">{v!.toFixed(1)}</span></span>)}</span>}
      </div>
    );
  }
  if (result.kind === "reviews") {
    return <p className="text-sm"><span className="font-semibold tabular-nums">{result.reviewCount}</span> <span className="text-muted-foreground">reviews on file</span></p>;
  }
  return <p className="text-sm text-muted-foreground">—</p>;
}

function ResultStatus({ result }: { result: PlaceResult }) {
  const map: Record<string, { label: string; className: string }> = {
    found: { label: "Found", className: "bg-emerald-500/90 text-white" },
    live: { label: "Live", className: "bg-emerald-500/90 text-white" },
    pass: { label: "Pass", className: "bg-emerald-500/90 text-white" },
    classified: { label: "Done", className: "bg-emerald-500/90 text-white" },
    flag: { label: "Flag", className: "bg-amber-500/90 text-black" },
    cut: { label: "Cut", className: "bg-red-500/90 text-white" },
    none: { label: "Not found", className: "bg-amber-500/20 text-amber-700 dark:text-amber-300" },
    "not-run": { label: "Not run", className: "bg-muted text-muted-foreground" },
    missing: { label: "—", className: "bg-muted text-muted-foreground" },
  };
  const tone = map[result.status] ?? { label: result.status, className: "bg-muted text-muted-foreground" };
  return <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold ${tone.className}`}>{tone.label}</span>;
}

function StateBadge({ state }: { state: string }) {
  return <span className="inline-flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs font-medium"><span className={`size-2 rounded-full ${stateDot(state)}`} />{state}</span>;
}

function Chips({ values }: { values: string[] }) {
  return <div className="flex flex-wrap gap-1">{values.map((v, i) => <span key={`${v}-${i}`} className="rounded-full border px-1.5 py-0.5 text-[11px]">{v}</span>)}</div>;
}

function Dot() {
  return <span className="text-muted-foreground/40">·</span>;
}

function arr(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
}

function duration(started: unknown, finished: unknown): string {
  const a = typeof started === "string" ? Date.parse(started) : NaN;
  const b = typeof finished === "string" ? Date.parse(finished) : Date.now();
  if (!Number.isFinite(a)) return "";
  const secs = Math.max(0, Math.round((b - a) / 1000));
  if (secs < 60) return `${secs}s`;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return s ? `${m}m ${s}s` : `${m}m`;
}

function stateDot(state: string): string {
  if (state === "done" || state === "complete") return "bg-emerald-500";
  if (state === "error" || state === "errored") return "bg-red-500";
  if (state === "running" || state === "queued" || state === "waiting") return "bg-amber-500";
  return "bg-muted-foreground";
}
